import React from "react";
import { Tag } from "antd";
import { BookOpen, Sparkles, ArrowRight, CheckCircle2, PlayCircle, Lock, Award, RotateCcw } from "lucide-react";

const LessonIntroView = ({
  onStart,
  lessons = [],
  watchedLessons = {},
  lessonsCompletedCount = 0,
  onSelectLesson,
  quizScore,
  totalQuizPoints,
  hasFinishedQuiz = false,
  isTier2 = false,
  isApprovedAmbassador = false,
  onProceedToNextStep,
}) => {
  const isLessonWatched = (lesson, idx) => {
    if (!lesson && idx === undefined) return false;
    return Boolean(isApprovedAmbassador) || Boolean(lesson && watchedLessons[lesson.id]) || Boolean(watchedLessons[idx]) || (Number(lessonsCompletedCount) > idx);
  };
  const completedCount = isApprovedAmbassador
    ? lessons.length
    : Math.max(
        lessons.filter((l, idx) => isLessonWatched(l, idx)).length,
        Number(lessonsCompletedCount) || 0
      );
  const firstUncompletedIndex = lessons.findIndex((l, idx) => !isLessonWatched(l, idx));
  const nextLessonIndex = firstUncompletedIndex !== -1 ? firstUncompletedIndex : 0;
  const isAllWatched = lessons.length > 0 && completedCount >= lessons.length;
  const showQuizResult = Boolean(hasFinishedQuiz || isApprovedAmbassador);
  const totalVal = Number(totalQuizPoints || (lessons.length ? lessons.length * 2 : 0)) || 0;
  const scoreVal = isApprovedAmbassador && !quizScore ? totalVal : Number(quizScore || 0);
  const percentage = totalVal > 0 ? Math.round((scoreVal / totalVal) * 100) : (scoreVal > 0 ? 100 : 0);

  return (
    <div className="space-y-5 text-left">
      <div className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-100 shadow-sm text-left">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center shadow-xs flex-shrink-0">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <span className="font-gilroyBold text-xs px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700">
              Official Orientation
            </span>
            {lessons.length > 0 && (
              <span className={`font-gilroyBold text-xs px-2.5 py-0.5 rounded-full border ${
                isAllWatched
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200/80"
                  : completedCount > 0
                  ? "bg-blue-50 text-blue-700 border-blue-200/80"
                  : "bg-gray-50 text-gray-600 border-gray-200/80"
              }`}>
                {completedCount} of {lessons.length} Completed
              </span>
            )}
            {showQuizResult && (
              <span className="font-gilroyBold text-xs px-2.5 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200/80">
                Quiz: {scoreVal}/{totalVal} ({percentage}%)
              </span>
            )}
          </div>
        </div>

        <h2 className="text-lg sm:text-xl font-gilroyBold text-gray-900 leading-tight m-0 tracking-tight">
          Ambassador Training & Lessons
        </h2>
        <p className="text-gray-600 text-[13px] m-0 mt-1 leading-relaxed">
          Watch all orientation modules and pass the qualifying quiz to unlock your official ambassador status.
        </p>

        {showQuizResult && (
          <div className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 mt-4 shadow-xs text-left">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gray-900 text-white flex items-center justify-center flex-shrink-0">
                  <Award className="w-4 h-4" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-gilroyBold text-gray-900 text-[13px]">
                      Quiz Assessment Result
                    </span>
                    <span className="font-gilroyBold text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                      Completed
                    </span>
                  </div>
                  <p className="text-gray-500 text-xs m-0 mt-0.5">
                    Official qualifying assessment completed
                  </p>
                </div>
              </div>

              <div className="text-left sm:text-right">
                <div className="font-gilroyBold text-base text-gray-900">
                  {scoreVal} / {totalVal} Points
                </div>
                <div className="text-xs text-gray-500 font-medium">
                  {percentage}% Passing Score
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="w-full bg-yellow-50/70 rounded-2xl p-3.5 mt-4 text-left space-y-1.5 border border-yellow-100/60">
          <div className="flex items-center gap-2 text-gray-900 font-gilroyBold text-[13px]">
            <div className="bg-yellow-200/80 rounded-full p-1">
              <Sparkles className="w-3.5 h-3.5 text-yellow-800" />
            </div>
            <span>What You Will Discover</span>
          </div>
          <ul className="text-gray-600 text-[13px] space-y-1 pl-5 list-disc m-0 leading-relaxed">
            <li>How to share your referral link and earn weekly income.</li>
            <li>How to earn 1% on user orders + 1.5% on business sales.</li>
            <li>How to onboard top restaurants and retail shops for up to ₦12,000 bonus.</li>
          </ul>
        </div>
      </div>

      {lessons.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <h3 className="font-gilroyBold text-base text-gray-900 m-0">
              Training Modules & Progress
            </h3>
            <span className="text-[13px] font-gilroyBold text-gray-700 bg-gray-100 px-2.5 py-0.5 rounded-full">
              {completedCount}/{lessons.length} Completed
            </span>
          </div>

          <div className="space-y-3">
            {lessons.map((lesson, idx) => {
              const isWatched = Boolean(isApprovedAmbassador) || isLessonWatched(lesson, idx);
              const isUnlocked = Boolean(isApprovedAmbassador) || idx === 0 || isLessonWatched(lessons[idx - 1], idx - 1);
              const isNext = !isApprovedAmbassador && idx === nextLessonIndex && !isWatched;

              return (
                <div
                  key={lesson.id || idx}
                  className={`p-4 rounded-2xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3.5 ${
                    isNext
                      ? "bg-white border-emerald-500 shadow-sm ring-1 ring-emerald-500/20"
                      : isUnlocked
                      ? "bg-white border-gray-100 shadow-xs hover:border-gray-200"
                      : "bg-gray-50/70 border-gray-200/60 opacity-60"
                  }`}
                >
                  <div className="flex items-start sm:items-center gap-3.5 min-w-0 flex-1">
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 font-gilroyBold text-sm ${
                        isWatched
                          ? "bg-gray-50 text-emerald-600 border border-gray-200/80"
                          : isNext
                          ? "bg-gray-900 text-white shadow-xs"
                          : isUnlocked
                          ? "bg-gray-100 text-gray-700"
                          : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      {isWatched ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      ) : isUnlocked ? (
                        <span>{idx + 1}</span>
                      ) : (
                        <Lock className="w-3.5 h-3.5 text-gray-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-0.5">
                        <span className="font-gilroyBold text-gray-900 text-[13px] leading-snug">
                          {lesson.title}
                        </span>
                        {isWatched && (
                          <span className="bg-gray-100 text-gray-700 font-gilroyBold text-xs px-2 py-0.5 rounded-full">
                            Completed
                          </span>
                        )}
                        {isNext && (
                          <span className="bg-blue-50 text-blue-700 font-gilroyBold text-xs px-2 py-0.5 rounded-full border border-blue-200/60">
                            Up Next
                          </span>
                        )}
                      </div>
                      <p className="text-gray-500 text-[13px] m-0 line-clamp-1 leading-relaxed">
                        {lesson.description || `Lesson ${idx + 1} video orientation`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 self-end sm:self-auto flex-shrink-0">
                    {isUnlocked ? (
                      <button
                        type="button"
                        onClick={() => onSelectLesson && onSelectLesson(idx)}
                        className={`px-3 py-1.5 rounded-xl text-[13px] font-gilroyBold border transition-all cursor-pointer flex items-center gap-1.5 ${
                          isWatched
                            ? "bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200 hover:text-gray-900"
                            : isNext
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white border-transparent shadow-xs"
                            : "bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200"
                        }`}
                      >
                        {isWatched ? (
                          <>
                            <RotateCcw className="w-3.5 h-3.5 text-gray-500" />
                            <span>Rewatch</span>
                          </>
                        ) : (
                          <>
                            <PlayCircle className="w-3.5 h-3.5" />
                            <span>Start Lesson {idx + 1}</span>
                          </>
                        )}
                      </button>
                    ) : (
                      <span className="text-[13px] text-gray-400 font-medium flex items-center gap-1.5">
                        <Lock className="w-3.5 h-3.5" />
                        Complete previous
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          if (isApprovedAmbassador) {
            if (onSelectLesson) onSelectLesson(0);
            else onStart();
          } else if (showQuizResult && onProceedToNextStep) {
            onProceedToNextStep();
          } else if (onSelectLesson && completedCount > 0 && !isAllWatched) {
            onSelectLesson(nextLessonIndex);
          } else {
            onStart();
          }
        }}
        className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-gilroyBold text-[13px] rounded-xl border-0 shadow-xs cursor-pointer transition flex items-center justify-center gap-2"
      >
        <span>
          {isApprovedAmbassador
            ? "Rewatch Orientation Lessons"
            : showQuizResult
            ? "View Application Status"
            : isAllWatched
            ? "Take Qualifying Quiz"
            : completedCount > 0
            ? `Continue Training (Start Lesson ${nextLessonIndex + 1})`
            : "Start Training"}
        </span>
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
};

export default LessonIntroView;
